import React from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ArrowRight, ShoppingBag, Trash2 } from "lucide-react";
import BotanicalBackdrop, { LeafGlyph } from "@/components/BotanicalBackdrop";
import ProductImage from "@/components/ProductImage";
import usePageTitle from "@/hooks/usePageTitle";
import { useCart, formatINR } from "@/context/CartContext";

const CartPage = () => {
  usePageTitle("Cart");
  const { items, subtotal, setQty, remove, totalShipped } = useCart();

  return (
    <>
      <section className="relative pt-16 pb-8 lg:pt-24 lg:pb-12 overflow-hidden">
        <BotanicalBackdrop variant="corners" />
        <div className="mx-auto max-w-4xl px-6 lg:px-10">
          <Link to="/products" className="inline-flex items-center gap-2 text-[13px] tracking-widest uppercase text-[var(--hk-ink-2)] hover:text-[var(--hk-orange-deep)]" data-testid="cart-back-btn">
            <ArrowLeft size={14} /> Continue Shopping
          </Link>
          <div className="mt-8">
            <div className="label-eyebrow">Your Cart</div>
            <h1 className="serif mt-3 text-[42px] sm:text-[52px] leading-[1.05] text-[var(--hk-ink)]" data-testid="cart-title">
              Your ritual, <span className="italic-accent text-[var(--hk-orange-deep)]">assembled.</span>
            </h1>
          </div>
        </div>
      </section>

      <section className="relative py-12">
        <div className="mx-auto max-w-4xl px-6 lg:px-10">
          {items.length === 0 ? (
            <div className="hk-card p-16 text-center" data-testid="cart-empty">
              <LeafGlyph className="mx-auto text-[var(--hk-gold-deep)] w-8 h-8" />
              <h2 className="serif mt-4 text-[26px] text-[var(--hk-ink)]">Your cart is quiet.</h2>
              <p className="mt-2 text-[14px] text-[var(--hk-ink-2)]">Explore the collection to begin your ritual.</p>
              <Link to="/products" className="hk-btn-primary mt-8 inline-flex" data-testid="cart-empty-shop-btn">
                Shop the Collection <ArrowRight size={16} />
              </Link>
            </div>
          ) : (
            <div className="space-y-4" data-testid="cart-items">
              {items.map((it) => (
                <div key={it.slug} className="hk-card p-5 flex items-center gap-5" data-testid={`cart-item-${it.slug}`}>
                  <Link to={`/products/${it.slug}`} className="w-24 h-24 shrink-0 rounded-[10px] overflow-hidden bg-[var(--hk-warm-ivory)]">
                    <ProductImage product={it.product} />
                  </Link>
                  <div className="flex-1 min-w-0">
                    <Link to={`/products/${it.slug}`} className="serif text-[18px] text-[var(--hk-ink)] hover:text-[var(--hk-orange-deep)] block">{it.product.name}</Link>
                    <div className="text-[12px] text-[var(--hk-ink-3)] mt-1 flex flex-wrap gap-3">
                      <span>{it.product.packSize}</span>
                      {it.isBogo && <span className="text-[var(--hk-orange-deep)]">· Ships {it.shippedUnits} bottles ({it.product.offer})</span>}
                    </div>
                    <div className="mt-3 inline-flex items-center border border-[var(--hk-border)] rounded-full bg-white">
                      <button onClick={() => setQty(it.slug, it.qty - 1)} className="w-9 h-9 flex items-center justify-center text-[var(--hk-ink-2)]" data-testid={`cart-decr-${it.slug}`}>−</button>
                      <span className="w-9 text-center tabular-nums" data-testid={`cart-qty-${it.slug}`}>{it.qty}</span>
                      <button onClick={() => setQty(it.slug, it.qty + 1)} className="w-9 h-9 flex items-center justify-center text-[var(--hk-ink-2)]" data-testid={`cart-incr-${it.slug}`}>+</button>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="serif text-[20px] text-[var(--hk-ink)]" data-testid={`cart-line-${it.slug}`}>{formatINR(it.linePrice)}</div>
                    <button onClick={() => remove(it.slug)} className="mt-2 text-[12px] text-red-600 inline-flex items-center gap-1 hover:underline" data-testid={`cart-remove-${it.slug}`}>
                      <Trash2 size={12} /> Remove
                    </button>
                  </div>
                </div>
              ))}

              {/* Summary */}
              <div className="hk-card p-8 mt-8" data-testid="cart-summary">
                <div className="flex items-center justify-between text-[14px] text-[var(--hk-ink-2)]">
                  <span>Subtotal</span>
                  <span className="text-[var(--hk-ink)]">{formatINR(subtotal)}</span>
                </div>
                <div className="flex items-center justify-between text-[14px] text-[var(--hk-ink-2)] mt-3">
                  <span>Shipping</span>
                  <span className="text-[var(--hk-ink)]">Calculated at checkout</span>
                </div>
                <div className="mt-4 pt-4 border-t border-[var(--hk-border)] flex items-center justify-between">
                  <span className="label-eyebrow">Estimated Total</span>
                  <span className="serif text-[26px] text-[var(--hk-ink)]" data-testid="cart-total">{formatINR(subtotal)}</span>
                </div>
                <div className="mt-2 text-[12px] text-[var(--hk-ink-3)]">
                  Total units to ship: <span className="text-[var(--hk-ink-2)] font-medium">{totalShipped}</span>
                </div>

                <Link to="/checkout" className="hk-btn-primary mt-6 w-full justify-center" data-testid="cart-checkout-btn">
                  Proceed to Checkout <ArrowRight size={16} />
                </Link>
              </div>
            </div>
          )}
        </div>
      </section>
    </>
  );
};

export default CartPage;
